import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '@/lib/api';
import { useBranding } from '@/hooks/useBranding';
import { useOidcPasswordGuard } from './useOidcPasswordGuard';

const API_BASE_URL = '/api/v1';

const resetSchema = z.object({
  newPassword: z.string().min(8),
  confirmPassword: z.string().min(8),
}).refine((data) => data.newPassword === data.confirmPassword, {
  path: ['confirmPassword'],
});

type ResetForm = z.infer<typeof resetSchema>;

interface ValidateResponse {
  valid: boolean;
  email?: string;
  reason?: 'invalid' | 'expired' | 'used';
}

async function fetchPublic<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, data);
  }
  const text = await response.text();
  if (!text) return {} as T;
  return JSON.parse(text) as T;
}

type PageState = 'loading' | 'form' | 'success' | 'error';

export default function ResetPasswordPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { branding } = useBranding();
  const brandedName = branding?.companyName;
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { blocked } = useOidcPasswordGuard();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
  });

  const validateToken = useCallback(async () => {
    if (!token) {
      setErrorMessage(t('resetPassword.tokenInvalid'));
      setPageState('error');
      return;
    }

    try {
      const result = await fetchPublic<ValidateResponse>(`/auth/validate-reset-token/${token}`);
      if (result.valid && result.email) {
        setEmail(result.email);
        setPageState('form');
      } else {
        switch (result.reason) {
          case 'expired':
            setErrorMessage(t('resetPassword.tokenExpired'));
            break;
          case 'used':
            setErrorMessage(t('resetPassword.tokenUsed'));
            break;
          default:
            setErrorMessage(t('resetPassword.tokenInvalid'));
        }
        setPageState('error');
      }
    } catch {
      setErrorMessage(t('resetPassword.tokenInvalid'));
      setPageState('error');
    }
  }, [token, t]);

  useEffect(() => {
    if (blocked) return;
    void validateToken();
  }, [validateToken, blocked]);

  const onSubmit = async (data: ResetForm) => {
    if (!token) return;
    setSubmitting(true);
    setErrorMessage('');

    try {
      await fetchPublic('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({
          token,
          newPassword: data.newPassword,
        }),
      });
      setPageState('success');
      setTimeout(() => { void navigate('/login', { replace: true }); }, 3000);
    } catch (err) {
      if (err instanceof ApiError) {
        const errData = err.data as { error?: string } | undefined;
        if (errData?.error?.includes('expired')) {
          setErrorMessage(t('resetPassword.tokenExpired'));
        } else if (errData?.error?.includes('used')) {
          setErrorMessage(t('resetPassword.tokenUsed'));
        } else {
          setErrorMessage(t('errors.generic'));
        }
      } else {
        setErrorMessage(t('errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 440, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            color="primary"
            textAlign="center"
            gutterBottom
          >
            {brandedName || t('appName')}
          </Typography>

          {pageState === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {pageState === 'error' && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
              <Button
                variant="outlined"
                onClick={() => navigate('/forgot-password')}
              >
                {t('resetPassword.requestNewLink')}
              </Button>
            </Box>
          )}

          {pageState === 'success' && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('resetPassword.resetSuccess')}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                {t('resetPassword.resetSuccessDesc')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('resetPassword.redirectingToLogin')}
              </Typography>
            </Box>
          )}

          {pageState === 'form' && (
            <>
              <Typography variant="h6" textAlign="center" gutterBottom>
                {t('resetPassword.title')}
              </Typography>

              <TextField
                value={email}
                label={t('resetPassword.emailLabel')}
                fullWidth
                disabled
                sx={{ mb: 2 }}
              />

              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}

              <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
                <TextField
                  {...register('newPassword')}
                  label={t('resetPassword.newPassword')}
                  type="password"
                  fullWidth
                  error={!!errors.newPassword}
                  helperText={errors.newPassword ? t('resetPassword.passwordMinLength') : undefined}
                  autoFocus
                  sx={{ mb: 2 }}
                />
                <TextField
                  {...register('confirmPassword')}
                  label={t('resetPassword.confirmPassword')}
                  type="password"
                  fullWidth
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword ? t('resetPassword.passwordMismatch') : undefined}
                  sx={{ mb: 3 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : t('resetPassword.resetButton')}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
